import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BoundingBox,
  ChatCircleDots,
  FileText,
  Gear,
  Plus,
  SpeakerHigh,
  SpeakerSlash,
  Trash,
  ImageSquare,
  PlayCircle,
  FolderOpen,
} from "@phosphor-icons/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import GridLayout, { Layout, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { DashboardService } from "../../service/DashboardService";
import { useNotificationStore } from "../../store/storeNotifications";
import useFlowsStore from "../../store/storeFlows";
import ChatWidget from "../Dashboard/Widgets/ChatWidget";
import WidgetShell from "../Dashboard/Widgets/WidgetShell";
import { FlowPreview } from "./Components/FlowPreview";
import { WidgetSelectionModal } from "./Components/WidgetSelectionModal";
import { useDashboard, Widget } from "./Context/DashboardContext";
import { ActionTypes } from "./Reducer";
import DashboardHeader from "./Components/DashboardHeader";
import FluxTutorials from "./Components/FluxTutorials";
import FlowsSection from "./Components/FlowsSection";
import { InterfaceWidget } from "./Components/InterfaceWidget";
import { useEntityExists } from "./hooks/useEntityExists";
import { DeletedEntityMessage } from "./Components/DeletedEntityMessage";

const ResponsiveReactGridLayout = WidthProvider(GridLayout) as any;

const getWidgetIcon = (type: string) => {
  switch (type) {
    case "chatbot":
      return <ChatCircleDots size={18} weight="regular" />;
    case "form":
      return <FileText size={18} weight="duotone" />;
    case "flow":
      return <BoundingBox size={18} weight="duotone" />;
    case "header":
      return <ImageSquare size={18} weight="duotone" />;
    case "tutorials":
      return <PlayCircle size={18} weight="duotone" />;
    case "flows":
      return <FolderOpen size={18} weight="duotone" />;
    default:
      return null;
  }
};

const WidgetComponent = memo(
  ({
    widget,
    onRemove,
    onExpand,
    onMinimize,
    isMinimized,
    onHoverMinimized,
    onLeaveMinimized,
  }: {
    widget: Widget;
    onRemove: () => void;
    onExpand: (widgetId: string) => void;
    onMinimize: (widgetId: string) => void;
    isMinimized: boolean;
    onHoverMinimized?: (widgetId: string) => void;
    onLeaveMinimized?: () => void;
  }) => {
    const [showChatSettings, setShowChatSettings] = useState(false);
    const [chatbotState, setChatbotState] = useState<any>(null);
    const chatSettingsBtnRef = useRef<HTMLButtonElement>(null);
    const chatSettingsDropdownRef = useRef<HTMLDivElement>(null);
    const chatWidgetRef = useRef<any>(null);

    const entityId = useMemo(() => {
      if (widget.type === "chatbot") return widget.chatbotData?.id;
      if (widget.type === "flow") return widget.flowData?.id;
      if (widget.type === "form") return widget.flowData?.id;
      return undefined;
    }, [widget.type, widget.chatbotData?.id, widget.flowData?.id]);

    const entityType = useMemo(() => {
      if (widget.type === "chatbot") return "chatbot" as const;
      if (widget.type === "form") return "form" as const;
      return "flow" as const;
    }, [widget.type]);

    const needsCheck =
      widget.type === "chatbot" ||
      widget.type === "flow" ||
      (widget.type === "form" && !!widget.flowData?.id);
    const { isDeleted, isChecking } = useEntityExists(
      needsCheck ? entityId : undefined,
      entityType,
    );

    useEffect(() => {
      if (!showChatSettings) return;
      const handlePointerDown = (e: PointerEvent) => {
        const target = e.target as Node;
        if (
          chatSettingsDropdownRef.current &&
          chatSettingsDropdownRef.current.contains(target)
        )
          return;
        if (
          chatSettingsBtnRef.current &&
          chatSettingsBtnRef.current.contains(target)
        )
          return;
        setShowChatSettings(false);
      };
      document.addEventListener("pointerdown", handlePointerDown);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown);
      };
    }, [showChatSettings]);

    const handleChatMenuAction = (action: string, data?: any) => {
      if (chatWidgetRef.current) {
        switch (action) {
          case "playAudio":
            chatWidgetRef.current.handlePlayChatResponse?.();
            break;
          case "toggleAutoAudio":
            chatWidgetRef.current.handleVoiceToggle?.();
            break;
          case "clearChat":
            chatWidgetRef.current.handleClearConversation?.();
            break;
          case "newChat":
            chatWidgetRef.current.handleCreateNewChat?.();
            break;
          case "changeSession":
            chatWidgetRef.current.handleSessionChange?.(data);
            break;
        }
      }
      setShowChatSettings(false);
    };

    const handleGetChatbotState = () => {
      if (chatWidgetRef.current) {
        const state = chatWidgetRef.current.getState?.();
        setChatbotState(state);
        return state;
      }
      return chatbotState;
    };

    useEffect(() => {
      if (showChatSettings && widget.type === "chatbot") {
        handleGetChatbotState();
      }
    }, [showChatSettings, widget.type]);

    const isHeaderWidget = widget.type === "header";

    const formUrl = useMemo(() => {
      if (widget.type === "form" && widget.formData?.url) {
        return widget.formData.url;
      }
      return null;
    }, [widget.type, widget.formData?.url]);

    const flowUrl = useMemo(() => {
      if (widget.type === "flow" && widget.flowData) {
        return (
          widget.flowData.url ||
          `${window.location.origin}/flow/${widget.flowData.id}`
        );
      }
      return null;
    }, [widget.type, widget.flowData?.url, widget.flowData?.id]);

    const deletedEntityLabel = useMemo(() => {
      if (widget.type === "chatbot") return "chatbot" as const;
      if (widget.type === "flow") return "agent" as const;
      return "form" as const;
    }, [widget.type]);

    const deletedEntityName = useMemo(() => {
      if (widget.type === "chatbot") return widget.chatbotData?.name;
      if (widget.type === "flow") return widget.flowData?.name;
      if (widget.type === "form")
        return widget.flowData?.name || widget.formData?.name;
      return undefined;
    }, [
      widget.type,
      widget.chatbotData?.name,
      widget.flowData?.name,
      widget.formData?.name,
    ]);

    return (
      <WidgetShell
        title={isHeaderWidget ? "" : widget.title}
        spaceName={widget.spaceName}
        icon={isHeaderWidget ? undefined : getWidgetIcon(widget.type)}
        onRemove={onRemove}
        onToggleExpand={() => onExpand(widget.id!)}
        onToggleMinimize={() => onMinimize(widget.id!)}
        isMinimized={isMinimized}
        hideMinimize={isHeaderWidget}
        hideExpand={isHeaderWidget}
        noPadding={isHeaderWidget}
        onMouseEnter={
          isMinimized && onHoverMinimized
            ? () => onHoverMinimized(widget.id!)
            : undefined
        }
        onMouseLeave={
          isMinimized && onLeaveMinimized ? onLeaveMinimized : undefined
        }
      >
        {showChatSettings && widget.type === "chatbot" && (
          <div
            ref={chatSettingsDropdownRef}
            className="chat-settings-dropdown"
            style={{
              position: "absolute",
              top: "40px",
              right: widget.type === "chatbot" ? "36px" : "8px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              padding: "4px",
              zIndex: 20,
              minWidth: "280px",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <button
              className="chat-settings-btn"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                cursor: chatbotState?.chatbot?.chatbotVoice
                  ? "pointer"
                  : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: chatbotState?.chatbot?.chatbotVoice ? "#333" : "#999",
                fontSize: "14px",
                borderRadius: "4px",
                textAlign: "left",
              }}
              onClick={() => handleChatMenuAction("toggleAutoAudio")}
              disabled={!chatbotState?.chatbot?.chatbotVoice}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span>
                {chatbotState?.isAutoPlayEnabled
                  ? "Autoplay Audio Enabled"
                  : "Autoplay Audio Disabled"}
              </span>
              {chatbotState?.isAutoPlayEnabled ? (
                <SpeakerHigh size={16} />
              ) : (
                <SpeakerSlash size={16} />
              )}
            </button>

            <button
              className="chat-settings-btn"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#333",
                fontSize: "14px",
                borderRadius: "4px",
                textAlign: "left",
              }}
              onClick={() => handleChatMenuAction("newChat")}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span>New Chat</span>
              <Plus size={16} />
            </button>

            <button
              className="chat-settings-btn"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#333",
                fontSize: "14px",
                borderRadius: "4px",
                textAlign: "left",
              }}
              onClick={() => handleChatMenuAction("clearChat")}
              disabled={(chatbotState?.messages?.length ?? 0) === 0}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span>Clear Chat</span>
              <Trash size={16} />
            </button>

            <div
              style={{
                borderTop: "1px solid #eee",
                margin: "4px 0",
                padding: "4px 0",
              }}
            >
              <div
                style={{
                  padding: "4px 12px",
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                }}
              >
                Sessions
              </div>
              <button
                className="chat-settings-btn"
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color:
                    chatbotState?.sessionId === "default" ? "#3650DE" : "#333",
                  fontSize: "14px",
                  borderRadius: "4px",
                  textAlign: "left",
                }}
                onClick={() => handleChatMenuAction("changeSession", "default")}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <span>Primary</span>
              </button>

              {chatbotState?.groupedSessionsByDate &&
                Object.entries(chatbotState.groupedSessionsByDate).map(
                  ([date, chats]: [string, any]) => (
                    <div key={date}>
                      <div
                        style={{
                          padding: "4px 12px",
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "600",
                        }}
                      >
                        {date}
                      </div>
                      {chats.map((chat: any) => (
                        <button
                          key={chat.id}
                          className="chat-settings-btn"
                          style={{
                            width: "100%",
                            background: "transparent",
                            border: "none",
                            padding: "8px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color:
                              chat.id === chatbotState.sessionId
                                ? "#3650DE"
                                : "#333",
                            fontSize: "14px",
                            borderRadius: "4px",
                            textAlign: "left",
                          }}
                          onClick={() =>
                            handleChatMenuAction("changeSession", chat.id)
                          }
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <span>{chat.name}</span>
                        </button>
                      ))}
                    </div>
                  ),
                )}
            </div>
          </div>
        )}

        {needsCheck && isDeleted && !isChecking ? (
          <DeletedEntityMessage
            entityType={deletedEntityLabel}
            entityName={deletedEntityName}
          />
        ) : widget.type === "chatbot" && widget.chatbotData ? (
          <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
            <ChatWidget
              ref={chatWidgetRef}
              chatbotData={widget.chatbotData}
              hideHeader
              onMenuAction={handleChatMenuAction}
              onGetChatbotState={handleGetChatbotState}
            />
          </div>
        ) : widget.type === "header" ? (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              overflow: "hidden",
            }}
          >
            <DashboardHeader />
          </div>
        ) : widget.type === "tutorials" ? (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              overflow: "hidden",
            }}
          >
            <FluxTutorials />
          </div>
        ) : widget.type === "flows" ? (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              overflow: "hidden",
            }}
          >
            <FlowsSection />
          </div>
        ) : widget.type === "form" && widget.flowData?.id ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <InterfaceWidget formId={widget.flowData.id} />
          </Box>
        ) : widget.type === "form" && formUrl ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              component="iframe"
              key={`form-${widget.id}`}
              src={formUrl}
              sx={{
                width: "100%",
                height: "100%",
                border: "none",
                flex: 1,
                minHeight: 0,
              }}
              title="Form Preview"
            />
          </Box>
        ) : widget.type === "flow" && widget.flowData ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <FlowPreview
              nodes={widget.flowData.nodes}
              edges={widget.flowData.edges}
              width="100%"
              height="100%"
            />
          </Box>
        ) : (
          <div style={{ color: "#666", fontSize: "12px" }}>
            {`${widget.type} widget content will go here`}
          </div>
        )}
      </WidgetShell>
    );
  },
);

const ExpandedWidgetComponent = memo(
  ({
    widget,
    onCollapse,
    onRemove,
  }: {
    widget: Widget;
    onCollapse: () => void;
    onRemove: () => void;
  }) => {
    const [showChatSettings, setShowChatSettings] = useState(false);
    const [chatbotState, setChatbotState] = useState<any>(null);
    const chatSettingsBtnRef = useRef<HTMLButtonElement>(null);
    const chatSettingsDropdownRef = useRef<HTMLDivElement>(null);
    const chatWidgetRef = useRef<any>(null);

    const entityId = useMemo(() => {
      if (widget.type === "chatbot") return widget.chatbotData?.id;
      if (widget.type === "flow") return widget.flowData?.id;
      if (widget.type === "form") return widget.flowData?.id;
      return undefined;
    }, [widget.type, widget.chatbotData?.id, widget.flowData?.id]);

    const entityType = useMemo(() => {
      if (widget.type === "chatbot") return "chatbot" as const;
      if (widget.type === "form") return "form" as const;
      return "flow" as const;
    }, [widget.type]);

    const needsCheck =
      widget.type === "chatbot" ||
      widget.type === "flow" ||
      (widget.type === "form" && !!widget.flowData?.id);
    const { isDeleted, isChecking } = useEntityExists(
      needsCheck ? entityId : undefined,
      entityType,
    );

    const deletedEntityLabel = useMemo(() => {
      if (widget.type === "chatbot") return "chatbot" as const;
      if (widget.type === "flow") return "agent" as const;
      return "form" as const;
    }, [widget.type]);

    const deletedEntityName = useMemo(() => {
      if (widget.type === "chatbot") return widget.chatbotData?.name;
      if (widget.type === "flow") return widget.flowData?.name;
      if (widget.type === "form")
        return widget.flowData?.name || widget.formData?.name;
      return undefined;
    }, [
      widget.type,
      widget.chatbotData?.name,
      widget.flowData?.name,
      widget.formData?.name,
    ]);

    useEffect(() => {
      if (!showChatSettings) return;
      const handlePointerDown = (e: PointerEvent) => {
        const target = e.target as Node;
        if (
          chatSettingsDropdownRef.current &&
          chatSettingsDropdownRef.current.contains(target)
        )
          return;
        if (
          chatSettingsBtnRef.current &&
          chatSettingsBtnRef.current.contains(target)
        )
          return;
        setShowChatSettings(false);
      };
      document.addEventListener("pointerdown", handlePointerDown);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown);
      };
    }, [showChatSettings]);

    const handleChatMenuAction = (action: string, data?: any) => {
      if (chatWidgetRef.current) {
        switch (action) {
          case "playAudio":
            chatWidgetRef.current.handlePlayChatResponse?.();
            break;
          case "toggleAutoAudio":
            chatWidgetRef.current.handleVoiceToggle?.();
            break;
          case "clearChat":
            chatWidgetRef.current.handleClearConversation?.();
            break;
          case "newChat":
            chatWidgetRef.current.handleCreateNewChat?.();
            break;
          case "changeSession":
            chatWidgetRef.current.handleSessionChange?.(data);
            break;
        }
      }
      setShowChatSettings(false);
    };

    const handleGetChatbotState = () => {
      if (chatWidgetRef.current) {
        const state = chatWidgetRef.current.getState?.();
        setChatbotState(state);
        return state;
      }
      return chatbotState;
    };

    useEffect(() => {
      if (showChatSettings && widget.type === "chatbot") {
        handleGetChatbotState();
      }
    }, [showChatSettings, widget.type]);

    const formUrl = useMemo(() => {
      if (widget.type === "form" && widget.formData?.url) {
        return widget.formData.url;
      }
      return null;
    }, [widget.type, widget.formData?.url]);

    const headerRightActions =
      widget.type === "chatbot" ? (
        <button
          ref={chatSettingsBtnRef}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            width: 28,
            height: 28,
            flex: "0 0 28px",
          }}
          onClick={() => setShowChatSettings(!showChatSettings)}
          onMouseDown={(e) => e.stopPropagation()}
          title="Chat settings"
        >
          <Gear size={16} color="#667085" />
        </button>
      ) : null;

    // Special handling for header widget - no title, no icon, only delete and drag
    const isHeaderWidget = widget.type === "header";

    return (
      <WidgetShell
        title={
          isHeaderWidget
            ? ""
            : widget.type === "chatbot" && widget.chatbotData
              ? widget.chatbotData.name
              : widget.title
        }
        icon={isHeaderWidget ? undefined : getWidgetIcon(widget.type)}
        onRemove={onRemove}
        onToggleExpand={onCollapse}
        onToggleMinimize={() => {}}
        isExpanded
        rightActions={headerRightActions}
        hideMinimize={isHeaderWidget}
        hideExpand={isHeaderWidget}
        noPadding={isHeaderWidget}
      >
        {showChatSettings && widget.type === "chatbot" && (
          <div
            ref={chatSettingsDropdownRef}
            className="chat-settings-dropdown"
            style={{
              position: "absolute",
              top: "40px",
              right: "36px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              padding: "4px",
              zIndex: 20,
              minWidth: "280px",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <button
              className="chat-settings-btn"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                cursor: chatbotState?.chatbot?.chatbotVoice
                  ? "pointer"
                  : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: chatbotState?.chatbot?.chatbotVoice ? "#333" : "#999",
                fontSize: "14px",
                borderRadius: "4px",
                textAlign: "left",
              }}
              onClick={() => handleChatMenuAction("toggleAutoAudio")}
              disabled={!chatbotState?.chatbot?.chatbotVoice}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span>
                {chatbotState?.isAutoPlayEnabled
                  ? "Autoplay Audio Enabled"
                  : "Autoplay Audio Disabled"}
              </span>
              {chatbotState?.isAutoPlayEnabled ? (
                <SpeakerHigh size={16} />
              ) : (
                <SpeakerSlash size={16} />
              )}
            </button>

            <button
              className="chat-settings-btn"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#333",
                fontSize: "14px",
                borderRadius: "4px",
                textAlign: "left",
              }}
              onClick={() => handleChatMenuAction("newChat")}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span>New Chat</span>
              <Plus size={16} />
            </button>

            <button
              className="chat-settings-btn"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "#333",
                fontSize: "14px",
                borderRadius: "4px",
                textAlign: "left",
              }}
              onClick={() => handleChatMenuAction("clearChat")}
              disabled={(chatbotState?.messages?.length ?? 0) === 0}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span>Clear Chat</span>
              <Trash size={16} />
            </button>

            <div
              style={{
                borderTop: "1px solid #eee",
                margin: "4px 0",
                padding: "4px 0",
              }}
            >
              <div
                style={{
                  padding: "4px 12px",
                  fontSize: "12px",
                  color: "#666",
                  fontWeight: "600",
                }}
              >
                Sessions
              </div>
              <button
                className="chat-settings-btn"
                style={{
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  padding: "8px 12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  color:
                    chatbotState?.sessionId === "default" ? "#3650DE" : "#333",
                  fontSize: "14px",
                  borderRadius: "4px",
                  textAlign: "left",
                }}
                onClick={() => handleChatMenuAction("changeSession", "default")}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <span>Primary</span>
              </button>

              {chatbotState?.groupedSessionsByDate &&
                Object.entries(chatbotState.groupedSessionsByDate).map(
                  ([date, chats]: [string, any]) => (
                    <div key={date}>
                      <div
                        style={{
                          padding: "4px 12px",
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "600",
                        }}
                      >
                        {date}
                      </div>
                      {chats.map((chat: any) => (
                        <button
                          key={chat.id}
                          className="chat-settings-btn"
                          style={{
                            width: "100%",
                            background: "transparent",
                            border: "none",
                            padding: "8px 12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            color:
                              chat.id === chatbotState.sessionId
                                ? "#3650DE"
                                : "#333",
                            fontSize: "14px",
                            borderRadius: "4px",
                            textAlign: "left",
                          }}
                          onClick={() =>
                            handleChatMenuAction("changeSession", chat.id)
                          }
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <span>{chat.name}</span>
                        </button>
                      ))}
                    </div>
                  ),
                )}
            </div>
          </div>
        )}

        <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
          {needsCheck && isDeleted && !isChecking ? (
            <DeletedEntityMessage
              entityType={deletedEntityLabel}
              entityName={deletedEntityName}
            />
          ) : widget.type === "chatbot" && widget.chatbotData ? (
            <ChatWidget
              ref={chatWidgetRef}
              chatbotData={widget.chatbotData}
              onMenuAction={handleChatMenuAction}
              onGetChatbotState={handleGetChatbotState}
            />
          ) : widget.type === "header" ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                overflow: "hidden",
              }}
            >
              <DashboardHeader />
            </div>
          ) : widget.type === "tutorials" ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                overflow: "auto",
              }}
            >
              <FluxTutorials />
            </div>
          ) : widget.type === "flows" ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                overflow: "auto",
              }}
            >
              <FlowsSection />
            </div>
          ) : widget.type === "form" && widget.flowData?.id ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <InterfaceWidget formId={widget.flowData.id} />
            </Box>
          ) : widget.type === "form" && formUrl ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Box
                component="iframe"
                key={`form-${widget.id}`}
                src={formUrl}
                sx={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  flex: 1,
                  minHeight: 0,
                }}
                title="Form Preview"
              />
            </Box>
          ) : widget.type === "flow" && widget.flowData ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <FlowPreview
                nodes={widget.flowData.nodes}
                edges={widget.flowData.edges}
                width="100%"
                height="100%"
              />
            </Box>
          ) : (
            <div
              style={{
                color: "#666",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              {`${widget.type} widget content will go here`}
            </div>
          )}
        </div>
      </WidgetShell>
    );
  },
);

export default function DashboardContent() {
  const theme = useTheme();
  const { semanticColors } = theme;
  const setSaving = useFlowsStore((state) => state.setSaving);
  const {
    state,
    dispatch,
    loadingDashboard,
    modalOpen,
    setModalOpen,
    removeWidget,
    addWidget,
  } = useDashboard();
  const [expandedWidgetId, setExpandedWidgetId] = useState<string | null>(null);
  const [minimizedWidgetIds, setMinimizedWidgetIds] = useState<Set<string>>(
    new Set(),
  );
  const [originalLayout, setOriginalLayout] = useState<Layout[]>([]);
  const [originalMinimizedLayouts, setOriginalMinimizedLayouts] = useState<
    Map<string, Layout>
  >(new Map());
  const [hoveredMinimizedWidget, setHoveredMinimizedWidget] = useState<
    string | null
  >(null);
  const isResizingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const setNotification = useNotificationStore(
    (state) => state.setNotification,
  );

  const calculateTitleWidth = (title: string): number => {
    const textWidth = title.length * 8;
    const totalWidth = textWidth + 80;
    return Math.min(Math.ceil(totalWidth / 80) + 1, 12);
  };

  const getLayoutFromWidgets = (widgets: Widget[]): Layout[] => {
    return widgets.map((widget) => widget.layout);
  };

  const handleHoverMinimizedWidget = (widgetId: string) => {
    if (!minimizedWidgetIds.has(widgetId)) return;

    setHoveredMinimizedWidget(widgetId);
    const widget = state.dashboard.widgets.find((w) => w.id === widgetId);
    if (widget) {
      const title =
        widget.type === "chatbot" && widget.chatbotData
          ? widget.chatbotData.name
          : widget.title;
      const requiredWidth = calculateTitleWidth(title);

      const updatedLayout = state.dashboard.widgets.map((w) =>
        w.id === widgetId ? { ...w.layout, w: requiredWidth } : w.layout,
      );

      dispatch({
        type: ActionTypes.UPDATE_LAYOUT,
        payload: updatedLayout,
      });
    }
  };

  const handleLeaveMinimizedWidget = () => {
    if (!hoveredMinimizedWidget) return;

    const widgetId = hoveredMinimizedWidget;
    setHoveredMinimizedWidget(null);

    const updatedLayout = state.dashboard.widgets.map((w) =>
      w.id === widgetId ? { ...w.layout, w: 2, h: 1 } : w.layout,
    );

    dispatch({
      type: ActionTypes.UPDATE_LAYOUT,
      payload: updatedLayout,
    });
  };

  const handleRemoveExpandedWidget = (widgetId: string) => {
    if (expandedWidgetId === widgetId) {
      setExpandedWidgetId(null);
      if (originalLayout.length > 0) {
        dispatch({
          type: ActionTypes.UPDATE_LAYOUT,
          payload: originalLayout,
        });
        setOriginalLayout([]);
      }
    }
    setTimeout(() => {
      removeWidget(widgetId);
    }, 0);
  };

  const handleAddWidgetClick = () => {
    setModalOpen(true);
  };

  const handleExpandWidget = (widgetId: string) => {
    if (expandedWidgetId === widgetId) {
      setExpandedWidgetId(null);
      if (originalLayout.length > 0) {
        dispatch({
          type: ActionTypes.UPDATE_LAYOUT,
          payload: originalLayout,
        });
        setOriginalLayout([]);
      }
    } else {
      setExpandedWidgetId(widgetId);
      const currentLayout = getLayoutFromWidgets(state.dashboard.widgets);
      setOriginalLayout([...currentLayout]);

      const updatedLayout = state.dashboard.widgets.map((w) =>
        w.id === widgetId
          ? { ...w.layout, x: 0, y: 0, w: 12, h: Math.max(w.layout.h, 8) }
          : w.layout,
      );

      dispatch({
        type: ActionTypes.UPDATE_LAYOUT,
        payload: updatedLayout,
      });
    }
  };

  const handleMinimizeWidget = (widgetId: string) => {
    const newMinimized = new Set(minimizedWidgetIds);
    const newOriginalLayouts = new Map(originalMinimizedLayouts);

    if (minimizedWidgetIds.has(widgetId)) {
      newMinimized.delete(widgetId);
      const originalLayout = newOriginalLayouts.get(widgetId);
      if (originalLayout) {
        const updatedLayout = state.dashboard.widgets.map((w) =>
          w.id === widgetId ? originalLayout : w.layout,
        );
        dispatch({
          type: ActionTypes.UPDATE_LAYOUT,
          payload: updatedLayout,
        });
        newOriginalLayouts.delete(widgetId);
      }
    } else {
      const widget = state.dashboard.widgets.find((w) => w.id === widgetId);
      if (widget) {
        newOriginalLayouts.set(widgetId, { ...widget.layout });
        newMinimized.add(widgetId);
        const updatedLayout = state.dashboard.widgets.map((w) =>
          w.id === widgetId ? { ...w.layout, w: 2, h: 1 } : w.layout,
        );
        dispatch({
          type: ActionTypes.UPDATE_LAYOUT,
          payload: updatedLayout,
        });
      }
    }

    setMinimizedWidgetIds(newMinimized);
    setOriginalMinimizedLayouts(newOriginalLayouts);
  };

  const handleLayoutChange = useCallback(
    (newLayout: Layout[]) => {
      if (!isResizingRef.current && !isDraggingRef.current) {
        dispatch({ type: ActionTypes.UPDATE_LAYOUT, payload: newLayout });
      }
    },
    [dispatch],
  );

  const handleResizeStart = useCallback(() => {
    isResizingRef.current = true;
  }, []);

  const handleResizeStop = useCallback(
    (newLayout: Layout[]) => {
      isResizingRef.current = false;
      dispatch({ type: ActionTypes.UPDATE_LAYOUT, payload: newLayout });
    },
    [dispatch],
  );

  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  const handleDragStop = useCallback(
    (newLayout: Layout[]) => {
      isDraggingRef.current = false;
      dispatch({ type: ActionTypes.UPDATE_LAYOUT, payload: newLayout });
    },
    [dispatch],
  );

  useEffect(() => {
    (window as any).addWidgetToDashboard = handleAddWidgetClick;

    return () => {
      delete (window as any).addWidgetToDashboard;
    };
  }, []);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initialize dashboard with default widgets if it's the only dashboard and empty
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    if (
      !state.dashboard.id ||
      !state.isInitialized ||
      state.dashboard.widgets.length > 0 ||
      !addWidget ||
      isInitializing
    )
      return;

    const initializeDefaultWidgets = async () => {
      setIsInitializing(true);
      try {
        // Check if this is the only dashboard
        const response = await DashboardService.getAllDashboards();
        const dashboards = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

        // Only initialize if it's the only dashboard
        if (dashboards.length === 1) {
          // Add widgets sequentially with proper positioning
          await addWidget({ type: "header" });
          await new Promise((resolve) => setTimeout(resolve, 200));
          await addWidget({ type: "tutorials" });
          await new Promise((resolve) => setTimeout(resolve, 200));
          await addWidget({ type: "flows" });
        }
      } catch (error) {
        console.error("Error initializing default widgets:", error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeDefaultWidgets();
  }, [
    state.dashboard.id,
    state.isInitialized,
    state.dashboard.widgets.length,
    addWidget,
    isInitializing,
  ]);

  useEffect(() => {
    if (
      !state.dashboard.id ||
      !state.isInitialized ||
      state.dashboard.widgets.length === 0
    )
      return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        setSaving({
          status: "loading",
          finished: false,
        });
        await DashboardService.updateBatchWidgets(state.dashboard.widgets);
        setSaving({
          status: "saved",
          finished: true,
          lastSaved: new Date().toISOString(),
        });
      } catch (error) {
        setSaving({
          status: "error",
          finished: false,
        });
        setNotification({
          type: "error",
          message: "Error updating dashboard",
        });

        console.error("Error updating dashboard:", error);
      }
    }, 500);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state.dashboard, state.isInitialized, setNotification]);

  if (loadingDashboard) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          backgroundColor: semanticColors.dashboardBackground,
          padding: "16px",
          overflow: "auto",
          position: "relative",

          "& .react-grid-item": {
            overflow: "visible !important",
            transition: "all 0.3s ease-in-out",
            willChange: "transform",
            backfaceVisibility: "hidden",
            transform: "translateZ(0)",
          },
          "& .react-grid-item > div": {
            height: "100%",
          },
          "& .react-grid-item.react-draggable-dragging": {
            transition: "none !important",
            zIndex: 1000,
          },
          "& .react-grid-item.react-resizable-resizing": {
            transition: "none !important",
            willChange: "width, height",
            contain: "layout style paint",
          },
          '& .react-grid-item[data-minimized="true"] .react-resizable-handle': {
            display: "none !important",
            pointerEvents: "none !important",
          },
          "& .widget-menu-btn": {
            display: "flex !important",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.1s ease-in-out",
          },
          "& .widget-expand-btn": {
            display: "flex !important",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.1s ease-in-out",
          },
          "& .widget-minimize-btn": {
            display: "flex !important",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.1s ease-in-out",
          },
          "& .widget-drag-handle": {
            display: "flex !important",
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.1s ease-in-out",
          },
          "& .widget-settings-btn": {
            display: "flex !important",
            opacity: 0,
            pointerEvents: "none",
          },
          "& .react-grid-item:hover .widget-menu-btn": {
            opacity: 1,
            pointerEvents: "auto",
          },
          '& .widget-menu-btn[aria-expanded="true"]': {
            opacity: "1 !important",
            pointerEvents: "auto !important",
          },
          "& .react-grid-item:hover .widget-expand-btn": {
            opacity: 1,
            pointerEvents: "auto",
          },
          "& .react-grid-item:hover .widget-minimize-btn": {
            opacity: 1,
            pointerEvents: "auto",
          },
          "& .react-grid-item:hover .widget-drag-handle": {
            opacity: 1,
            pointerEvents: "auto",
          },
          "& .react-grid-item:hover .widget-settings-btn": {
            opacity: 1,
            pointerEvents: "auto",
          },

          "& .widget-menu-btn:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
          "& .widget-expand-btn:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
          "& .widget-minimize-btn:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
          "& .widget-settings-btn:hover": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },

          "& .widget-remove-btn:hover": {
            backgroundColor: "rgba(255,68,68,0.1)",
          },
        }}
      >
        <ResponsiveReactGridLayout
          className="layout"
          layout={
            expandedWidgetId
              ? getLayoutFromWidgets(state.dashboard.widgets).filter(
                  (item) => item.i === expandedWidgetId,
                )
              : getLayoutFromWidgets(state.dashboard.widgets)
          }
          onLayoutChange={handleLayoutChange}
          onResizeStart={handleResizeStart}
          onResizeStop={handleResizeStop}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
          cols={12}
          rowHeight={80}
          isDraggable={!expandedWidgetId}
          isResizable={
            !expandedWidgetId &&
            minimizedWidgetIds.size === 0 &&
            !hoveredMinimizedWidget
          }
          margin={[8, 8]}
          containerPadding={[0, 0]}
          compactType="vertical"
          preventCollision={false}
          draggableHandle={".widget-drag-handle"}
          useCSSTransforms={true}
          transformScale={1}
          resizeHandles={["se", "sw", "ne", "nw", "s", "n", "e", "w"]}
          style={{
            transition: "all 0.3s ease-in-out",
            opacity: expandedWidgetId ? 0.95 : 1,
          }}
        >
          {expandedWidgetId
            ? state.dashboard.widgets
                .filter((widget) => widget.id === expandedWidgetId)
                .map((widget: Widget) => (
                  <div key={widget.id}>
                    <ExpandedWidgetComponent
                      widget={widget}
                      onCollapse={() => handleExpandWidget(widget.id!)}
                      onRemove={() => handleRemoveExpandedWidget(widget.id!)}
                    />
                  </div>
                ))
            : state.dashboard.widgets.map((widget: Widget) => {
                const isMinimized = minimizedWidgetIds.has(widget.id!);
                return (
                  <div
                    key={widget.id}
                    data-minimized={isMinimized ? "true" : "false"}
                  >
                    <WidgetComponent
                      widget={widget}
                      onRemove={() => removeWidget(widget.id!)}
                      onExpand={handleExpandWidget}
                      onMinimize={handleMinimizeWidget}
                      isMinimized={isMinimized}
                      onHoverMinimized={handleHoverMinimizedWidget}
                      onLeaveMinimized={handleLeaveMinimizedWidget}
                    />
                  </div>
                );
              })}
        </ResponsiveReactGridLayout>
      </Box>

      <WidgetSelectionModal
        open={modalOpen}
        onClose={() => setModalOpen(!modalOpen)}
      />
    </Box>
  );
}
